import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { HttpClientService } from '../../core/services/http/http-client.service';
import { Carriers } from '../../shared/enum/general-enum';
import { IContact } from '../../shared/models/contact.interface';
import { AddressBook } from '../models/address-book.model';

@Injectable()
export class AddressBookService {

    constructor(private httpClientService: HttpClientService,
                private apiEndpointService: ApiEndpointService) {
    }

    /**
     * Gets address book page.
     *
     * @param {string} pageIndex
     * @param {string} pageSize
     * @returns {Observable<IContact[]>}
     */
    public getAddressBookPage(pageIndex?: string, pageSize?: string): Observable<IContact[]> {
        const params = {};
        if (pageIndex) {
            params['page'] = pageIndex;
        }

        if (pageSize) {
            params['per_page'] = pageSize;
        }

        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getAddressBookPage'))
            .query(params)
            .toString();
        return this.httpClientService.get<IContact[]>(endpoint)
                   .pipe(
                       map(contacts => contacts.filter(contact => contact.Country !== 'PA')),
                   );
    }

    /**
     * Gets address book.
     *
     * @param {string} contactId
     * @returns {Observable<any>}
     */
    public getAddressBook(contactId: string): Observable<AddressBook> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getContactAddress'))
            .param('contactId', contactId)
            .toString();
        return this.httpClientService.get<AddressBook>(endpoint);
    }

    /**
     * Gets all countries.
     *
     * @returns {Observable<ICountry>}
     */
    public getCountries(): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCarrierCountries'))
            .query({
                carriercode: 'UPS',
                isEU: false,
                selectiontype: 'address',
            })
            .toString();
        return this.httpClientService.get<any>(endpoint);
    }

    /**
     * Saves a contact.
     *
     * @param {AddressBook} addressBook
     * @returns {Observable<any>}
     */
    public saveAddressBook(addressBook: AddressBook): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('saveAddressBook'))
            .toString();
        return this.httpClientService.post<any>(endpoint, addressBook);
    }

    /**
     * Shares an address book.
     *
     * @param {string} contactId
     * @param {boolean} isShared
     * @returns {Observable<any>}
     */
    public shareAddressBook(contactId: string, isShared: boolean): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('shareAddressBook'))
            .param('contactId', contactId)
            .param('isShared', isShared.toString())
            .toString();
        return this.httpClientService.post<any>(endpoint, {});
    }

    /**
     * Sets the address book express.
     *
     * @param {string} contactId
     * @param {boolean} isExpress
     * @returns {Observable<any>}
     */
    public expressAddressBook(contactId: string, isExpress: boolean): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('expressAddressBook'))
            .param('contactId', contactId)
            .param('isExpress', isExpress.toString())
            .toString();
        return this.httpClientService.post<any>(endpoint, {});
    }

    /**
     * Removes an address.
     *
     * @param {string} locationId
     * @returns {Observable<any>}
     */
    public removeAddress(locationId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('deleteAddressBook'))
            .param('contactId', locationId)
            .toString();
        return this.httpClientService.delete<any>(endpoint);
    }

    /**
     * Searches for address books.
     *
     * @param {string} searchText
     * @returns {Observable<IContact[]>}
     */
    public searchAddressBooks(searchText: string): Observable<IContact[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('searchCarrierAddressBook'))
            .query({
                query: searchText,
            })
            .toString();
        return this.httpClientService.get<IContact[]>(endpoint);
    }

    public searchCarrierAddressBook(carrier: Carriers, keyword: string, scope: string): Observable<IContact[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('searchCarrierAddressBook'))
            .query({
                carriercode: carrier.toString().toLowerCase(),
                query: keyword,
                column: scope,
            })
            .toString();

        return this.httpClientService.get<IContact[]>(endpoint)
                   .pipe(
                       map(contacts => contacts.filter(contact => contact.Country !== 'PA')),
                   );
    }

    public uploadAddressBook(importAddress: any[]): Observable<any> {
        const data = {
            AddressBookContacts: importAddress,
        };

        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('uploadAddressBook'))
            .toString();
        return this.httpClientService.post<any>(endpoint, data);
    }
}
